import {Button, Table, Tag, Space, Typography, Card, Input, Spin} from 'antd';
import React, {useEffect, useState} from 'react';
import {Link, useHistory} from 'react-router-dom';
import {
  PlusOutlined,
  SearchOutlined,
  PlayCircleOutlined,
  FileGifOutlined,
} from '@ant-design/icons';
import {StoryRepository} from './StoryRepository';
import {StoryService} from './StoryService';
import {
  Story,
  Media,
  MediaType,
} from '@PikaDoApp/npm-pikado-logic/dist/modules/story/graphql/story.interface';
import {storyStorageRepo} from './StoryStorageRepository';
const {Title, Text} = Typography;

const storyRepo = new StoryRepository();
const storyService = new StoryService(storyRepo);

const CoverImage = ({coverMedia}: {coverMedia: Media}) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const isVideo = coverMedia?.type === MediaType.VIDEO;
  const isAnimation = coverMedia?.type === MediaType.ANIMATION;

  useEffect(() => {
    if (!coverMedia) {
      setLoading(false);
      return;
    }

    const urlToResolve = coverMedia.mediaUrl;
    if (!urlToResolve) {
      setLoading(false);
      return;
    }

    storyStorageRepo
      .resolveUrl(urlToResolve)
      .then((url) => setSignedUrl(url))
      .catch((err) => console.error('Error resolving cover image:', err))
      .finally(() => setLoading(false));
  }, [coverMedia, isVideo]);

  if (loading) {
    return (
      <div
        style={{
          width: '50px',
          height: '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Spin size="small" />
      </div>
    );
  }

  // Render video
  if (isVideo && signedUrl) {
    return (
      <div
        style={{
          width: '50px',
          height: '50px',
          borderRadius: '8px',
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}>
        <video
          src={signedUrl}
          autoPlay
          loop
          style={{width: '100%', objectFit: 'contain', background: '#000'}}
        />
      </div>
    );
  }

  if (!signedUrl) {
    return (
      <div
        style={{
          width: '50px',
          height: '50px',
          borderRadius: '8px',
          background: '#f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px dashed #cbd5e1',
        }}>
        <Text type="secondary" style={{fontSize: '10px'}}>
          No Media
        </Text>
      </div>
    );
  }

  return (
    <div style={{position: 'relative', width: '50px', height: '50px'}}>
      <img
        src={signedUrl}
        alt="cover"
        style={{
          width: '50px',
          height: '50px',
          borderRadius: '8px',
          objectFit: 'cover',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      />
      {isVideo && (
        <div
          style={{
            position: 'absolute',
            bottom: '2px',
            right: '2px',
            background: 'rgba(0,0,0,0.6)',
            borderRadius: '4px',
            padding: '2px 4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <PlayCircleOutlined style={{color: '#fff', fontSize: '12px'}} />
        </div>
      )}
      {isAnimation && (
        <div
          style={{
            position: 'absolute',
            bottom: '2px',
            right: '2px',
            background: 'rgba(128,0,255,0.8)',
            borderRadius: '4px',
            padding: '1px 4px',
            fontSize: '8px',
            fontWeight: 'bold',
            color: '#fff',
          }}>
          GIF
        </div>
      )}
    </div>
  );
};

export const StoryTable = () => {
  const history = useHistory();
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      setLoading(true);
      const response = await storyService.getStories({first: 50});
      if (response && response.edges) {
        setStories(response.edges);
      }
    } catch (error) {
      console.error('Error fetching stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredStories = stories.filter(
    (story) =>
      story.title.en.toLowerCase().includes(searchText.toLowerCase()) ||
      (story.title.ar && story.title.ar.includes(searchText)),
  );

  const columns = [
    {
      title: 'Cover',
      dataIndex: 'coverMedia',
      key: 'coverMedia',
      render: (coverMedia: Media) => <CoverImage coverMedia={coverMedia} />,
    },
    {
      title: 'Title',
      key: 'title',
      render: (record: Story) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{fontSize: '16px'}}>
            {record.title.en}
          </Text>
          {record.title.ar && (
            <Text type="secondary" style={{fontSize: '12px'}}>
              {record.title.ar}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => <Tag color="blue">{category}</Tag>,
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: Story) => (
        <Tag color={record.isPublished ? 'green' : 'orange'}>
          {record.isPublished ? 'Published' : 'Draft'}
        </Tag>
      ),
    },
    {
      title: 'Chapters',
      key: 'chapters',
      render: (record: Story) => record.chapters?.length || 0,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Order',
      dataIndex: 'storyOrder',
      key: 'storyOrder',
      render: (order: number) => <Text strong>#{order}</Text>,
    },
  ];

  return (
    <div
      style={{
        padding: '24px',
        background: '#f0f2f5',
        minHeight: '100vh',
      }}>
      <Card
        bordered={false}
        style={{
          borderRadius: '16px',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.07)',
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.18)',
        }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
          }}>
          <div>
            <Title level={2} style={{margin: 0, color: '#1a1c2e'}}>
              Story Library
            </Title>
            <Text type="secondary">
              Manage and create immersive educational stories
            </Text>
          </div>
          <Link to="/stories/create">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              style={{
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                height: '45px',
                padding: '0 24px',
                fontWeight: 600,
              }}>
              New Story
            </Button>
          </Link>
        </div>

        <div style={{marginBottom: '20px'}}>
          <Input
            placeholder="Search stories by title..."
            prefix={<SearchOutlined style={{color: '#bfbfbf'}} />}
            size="large"
            allowClear
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              borderRadius: '12px',
              maxWidth: '400px',
            }}
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredStories}
          rowKey="_id"
          loading={loading}
          pagination={{pageSize: 10}}
          style={{
            borderRadius: '12px',
            overflow: 'hidden',
          }}
          rowClassName="premium-row"
          onRow={(record) => ({
            onClick: () => {
              history.push(`/stories/${record._id}`);
            },
          })}
        />
      </Card>

      <style>{`
        .premium-row {
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .premium-row:hover {
          background-color: rgba(102, 126, 234, 0.05) !important;
        }
        .ant-table-thead > tr > th {
          background: #fafafa;
          color: #1a1c2e;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};
